import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createMatterAssignee,
  deleteMatterAssigneeV1,
  updateMatterAssigneeV1,
} from "@/lib/data/requests/matters/matter-assignee";
import { queryKeys } from "@/lib/data/static";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

type MatterAssigneeMutationOptions = {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
};

const defaultMatterAssigneeMutationOptions = {
  invalidateOnSettled: true,
  showSuccessToast: true,
} satisfies Required<MatterAssigneeMutationOptions>;

export const useCreateMatterAssignee = (
  options: MatterAssigneeMutationOptions = defaultMatterAssigneeMutationOptions,
) => {
  const mutationOptions = {
    ...defaultMatterAssigneeMutationOptions,
    ...options,
  };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterAssignee>[0]) =>
      createMatterAssignee(data),
    onMutate: (data) => {
      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating matter assignee");
    },

    onSettled: async (_data, _error, variables) => {
      if (!mutationOptions.invalidateOnSettled) return;
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (!mutationOptions.showSuccessToast) return;
      toast.success("Matter assignee created successfully");
    },
  });
};

export const useUpdateMatterAssigneeEndTime = (
  options: MatterAssigneeMutationOptions = defaultMatterAssigneeMutationOptions,
) => {
  const mutationOptions = {
    ...defaultMatterAssigneeMutationOptions,
    ...options,
  };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterAssigneeV1>[0]) =>
      updateMatterAssigneeV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter assignee");
    },

    onSettled: async () => {
      if (!mutationOptions.invalidateOnSettled) return;
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (!mutationOptions.showSuccessToast) return;
      toast.success("Matter assignee updated successfully");
    },
  });
};

export const useDeleteMatterAssignee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterAssigneeV1>[0]) =>
      deleteMatterAssigneeV1(data),
    onError: () => {
      toast.error("Error deleting matter assignee");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter assignee deleted successfully");
    },
  });
};
